import {Component, EventEmitter, inject, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {
  BicButtonComponent,
  BicDropdownElementComponent,
  BicInputComponent,
  BicPopupComponent,
  BicSelectComponent,
  BicToggleComponent,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {CheckMode, Ruleset, RulesetType} from '../models/rule-management.models';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faClipboardCheck, faCube, faFile, faListCheck} from '@fortawesome/pro-solid-svg-icons';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {ChatService} from '../../chatBIC/service/chat.service';

/**
 * Was beim Anlegen eines myChecks aus dem Popup herauskommt.
 *
 * <p>Der Quickstart ist Teil der Entscheidung, nicht des Regelsets: er entscheidet nur, ob der
 * Editor anschließend chat-geführt startet oder als leeres Formular.
 *
 * <p>Bei aktivem Quickstart ist `ruleset.type` bewusst **nicht gesetzt**: seit 08/2026 entscheidet
 * der Chat-Agent selbst zwischen KI (Dokumentprüfung) und IDS (IFC-Modellprüfung), und der myCheck
 * entsteht erst mit dieser Entscheidung. Ein hier vorgegebener Typ wäre eine Vorwegnahme, die den
 * Agenten entweder einschränkt oder zu einem Datensatz in der falschen Tabelle führt.
 */
export interface RulesetCreationRequest {
  ruleset: Partial<Ruleset>;
  startInQuickstartMode: boolean;
}

interface RulesetTemplate {
  name: string;
  description: string;
  type: RulesetType;
  icon: IconDefinition;
}

@Component({
  selector: 'app-create-ruleset-prompt',
  standalone: true,
  imports: [
    BicPopupComponent,
    BicButtonComponent,
    BicInputComponent,
    BicSelectComponent,
    BicDropdownElementComponent,
    BicToggleComponent,
    FormsModule,
    FaIconComponent,
    TooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './create-ruleset-prompt.component.html',
})
export class CreateRulesetPromptComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();
  @Output() created = new EventEmitter<RulesetCreationRequest>();

  showTemplates = false;
  /**
   * Chat-geführte Erstellung nach dem Anlegen. Standard an – der Quickstart ist der schnellste Weg
   * zu einem vollständigen myCheck; abschaltbar für Nutzer, die direkt im Formular arbeiten wollen.
   */
  startInQuickstartMode = true;
  name = '';
  type: RulesetType = 'KI';

  typeOptions: { value: RulesetType; label: string }[] = [
    {value: 'KI', label: 'KI Prüfung'},
    {value: 'HYBRID', label: 'Hybride Prüfung'},
    {value: 'IDS', label: 'IDS Prüfung'},
  ];

  templates: RulesetTemplate[] = [
    {name: 'MBO-Prüfung', description: 'Prüfe die Einhaltung der Musterbauordnung', type: 'KI', icon: faListCheck},
    {name: 'LOIplus-Check', description: 'Prüfe die Einhaltung der Musterbauordnung', type: 'IDS', icon: faCube},
    {name: 'HOAI-Prüfung', description: 'Prüfe die Mindestinhalte nach HOAI', type: 'KI', icon: faClipboardCheck},
    {name: 'Raumbuch analysieren', description: 'Technischen Anforderung in Raumbüchern', type: 'HYBRID', icon: faFile},
  ];

  private chatService = inject(ChatService);

  /**
   * chatBIC-Kontingent des Projekts aufgebraucht – der Quickstart läuft über chatBIC und ist dann
   * nicht möglich. Quelle ist derselbe Stand wie für den Senden-Button im Chat
   * (`ChatService.chatQuota`), damit Popup und Chat nie Unterschiedliches sagen.
   */
  get chatQuotaExceeded(): boolean {
    return this.chatService.chatQuota.exceeded;
  }

  /**
   * Startet der myCheck tatsächlich im Quickstart? Die Wahl des Nutzers bleibt in
   * `startInQuickstartMode` erhalten; ein aufgebrauchtes Kontingent übersteuert sie nur.
   */
  get quickstartActive(): boolean {
    return this.startInQuickstartMode && !this.chatQuotaExceeded;
  }

  get quickstartDisabledTooltip(): string {
    if (!this.chatQuotaExceeded) {
      return '';
    }
    const owner = this.chatService.chatQuota.scope === 'ORGANISATION' ? ' Ihrer Organisation' : '';
    return `Quickstart nicht verfügbar: chatBIC-Kontingent${owner} aufgebraucht.`;
  }

  ngOnInit(): void {
    // Das Kontingent kann seit dem letzten Laden verbraucht worden sein (z. B. in einem anderen
    // Tab) – deshalb beim Öffnen des Popups frisch holen.
    this.chatService.refreshChatQuota();
  }

  onClose(): void {
    this.closed.emit();
  }

  selectTemplate(template: RulesetTemplate): void {
    this.name = template.name;
    this.type = template.type;
  }

  onCreate(): void {
    if (this.quickstartActive) {
      // Typ, Dateitypen und Prüfmodus hängen davon ab, ob es eine Dokument- oder eine
      // IFC-Modellprüfung wird – das entscheidet der Agent im Interview und legt den myCheck
      // danach selbst an. Hier wird deshalb nur der Name weitergegeben.
      this.created.emit({
        ruleset: {name: this.name.trim(), description: ''},
        startInQuickstartMode: true,
      });
      return;
    }

    this.created.emit({
      ruleset: {
        name: this.name.trim(),
        // Die Beschreibung entsteht erst bei der Konfiguration im Editor.
        description: '',
        type: this.type,
        allowedFileTypes: this.getDefaultFileTypes(this.type),
        checkMode: 'SINGLE' as CheckMode,
      },
      startInQuickstartMode: false,
    });
  }

  onQuickstartModeChange(enabled: boolean): void {
    this.startInQuickstartMode = enabled;
  }

  private getDefaultFileTypes(type: RulesetType): string[] {
    if (type === 'IDS' || type === 'HYBRID') {
      return ['.ifc'];
    }
    return ['.pdf'];
  }
}
